import { useCallback, useEffect, useState } from 'react';

import SettingsEditPanel from '../../controls/settings-edit-panel';
import RedirectUrlDetail from './redirect-url-detail';
import { useWorkingSession } from '@/utils/use-working-session';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import Container from '@ui/container/container';
import { validateUrl } from '@/utils/validate-url';

const RedirectUrlWidget = () => {
  const isSaving = useAppSelector(selectIsSavingWorkingProject);

  const session = useWorkingSession();

  const dispatch = useAppDispatch();

  const customUrlEnabled = !!session?.custom_url_enabled;
  const [customUrl, setCustomUrl] = useState(session?.custom_url || '');
  const [customUrlSaveDisabled, setCustomUrlSaveDisabled] = useState(true);
  const [invalidUrlError, setInvalidUrlError] = useState('');

  const handleSave = useCallback(() => {
    if (!session) return;

    dispatch(
      saveWorkingProjectSessionByField({
        uuid: session.uuid,
        custom_url: customUrl,
      })
    );
  }, [customUrl, dispatch, session]);

  const handleCancel = () => {
    setCustomUrl(session?.custom_url || '');
  };

  /* validate url */
  useEffect(() => {
    if (!session) return;
    setInvalidUrlError('');

    if (!customUrlEnabled) {
      setCustomUrlSaveDisabled(false);
    } else if (customUrl.includes(session.uuid)) {
      setInvalidUrlError("URL cannot point to the session you're redirecting from");
      setCustomUrlSaveDisabled(true);
    } else if (!customUrl) {
      // Allow them to type something before the error shows up
      // Admins will 99% of the time copy paste anyway
      setInvalidUrlError('');
      setCustomUrlSaveDisabled(true);
    } else if (customUrlEnabled && !validateUrl(customUrl)) {
      // customUrl is enabled and url is invalid
      setInvalidUrlError('Please enter a valid URL (e.g., https://www.example.com)');
      setCustomUrlSaveDisabled(true);
    } else {
      setCustomUrlSaveDisabled(false);
    }
  }, [customUrl, customUrlEnabled, session]);

  return (
    <Container loading={isSaving} opacity={isSaving ? 0.6 : 1}>
      <SettingsEditPanel
        isEditing={customUrlEnabled}
        onSave={handleSave}
        onCancel={handleCancel}
        saveDisabled={customUrlSaveDisabled}
        warningMessage="You will need to publish the project for this redirect to be applied."
        marginOnEdit={0}
      >
        <RedirectUrlDetail
          isEditing={customUrlEnabled}
          customUrl={customUrl}
          setCustomUrl={setCustomUrl}
          session={session}
          disabled={isSaving}
          invalidUrlError={invalidUrlError}
        />
      </SettingsEditPanel>
    </Container>
  );
};

export default RedirectUrlWidget;
